/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { createDataActionHook, IActionContext, IActionInput } from '@msdyn365-commerce/core';
import { IMSDyn365Window } from '@msdyn365-commerce/core-internal';
import { Cart, CartLine, IAddCartLinesParam, IDataServiceRequest } from '@msdyn365-commerce/retail-proxy';
import { IProductInfo, TelemetryEvent } from '@msdyn365-commerce/telemetry-internal';
import { getProductInfoFromCartLinesAsync } from './telemetry.action.helper';

declare let window: IMSDyn365Window;

/**
 * Saves info on cart lines added before calling data action
 * @param inputs Data action inputs
 * @param actionContext Action context
 */
const beforeAddToCart = async (inputs: IActionInput | IActionInput[], actionContext: IActionContext) => {
    try {
        const actionInput = Array.isArray(inputs) ? <IDataServiceRequest>inputs[0] : <IDataServiceRequest>inputs;

        // If cart lines were added
        if (actionInput.queryParams && typeof actionInput.queryParams === 'function') {
            const addCartLinesParams = actionInput.queryParams<IAddCartLinesParam>();
            if (addCartLinesParams && addCartLinesParams.cartLines) {
                // Save info on added cart lines for post data action hook to pick up
                actionContext.requestContext.telemetryData.addedCartLines = addCartLinesParams.cartLines;
                return;
            }
        }
        actionContext.telemetry.debug('AddToCart pre-data action hook failed - No added cart lines found');
    } catch (e) {
        actionContext.telemetry.debug('AddToCart pre-data action hook failed.', e);
    }
};

/**
 * Fires an AddToCart event after AddCartLines data action completed
 * @param _inputs Data action inputs
 * @param cart New cart object after cart lines have been added
 * @param actionContext Action context
 */
const afterAddToCart = async (_inputs: IActionInput | IActionInput[], cart: Cart | Cart[], actionContext: IActionContext) => {
    if (window && window._msdyn365 && window._msdyn365.telemetry) {
        const cartObject = Array.isArray(cart) ? cart[0] : cart;
        const addedCartLines = <CartLine[]>actionContext.requestContext.telemetryData.addedCartLines;
        let addedProducts: IProductInfo[] = [];

        // Collect info on products added to cart
        if (addedCartLines) {
            try {
                addedProducts = await getProductInfoFromCartLinesAsync(addedCartLines, actionContext);
            } catch (error) {
                actionContext.telemetry.error('AddCartLines post-data action hook failed to process cart lines.', error);
            }
        }

        // Fire AddToCart event
        window._msdyn365.telemetry.logEvent(TelemetryEvent.AddToCart, {
            cartId: cartObject.Id,
            cartVersion: cartObject.Version ? cartObject.Version.toString() : '',
            products: addedProducts,
            orderId: cartObject.OrderNumber ? cartObject.OrderNumber.toString() : ''
        });
    }
};

createDataActionHook({
    actionId: '@msdyn365-commerce/retail-proxy/Carts/AddCartLines',
    postReaderHook: afterAddToCart,
    preReaderHook: beforeAddToCart
});
